import React, { useState } from 'react';
import { Container } from 'reactstrap';
import Navbar from '../Navbar';
import Footer from '../Footer';
import GuestChatWidget from '../GuestChatWidget';
import '../landing.scss';
import SeoHead from '../../../../Components/Common/SeoHead';
import { MARKETS, calculateFees, FeeCalcOpts } from './feeRates';

const APP_URL = process.env.REACT_APP_APP_URL || '';

const FAQ_ITEMS = [
  {
    q: 'How much does eBay charge in fees?',
    a: 'eBay charges a Final Value Fee (a percentage of the total sale price including postage) plus a small fixed fee per order. In the UK most categories are around 12.9% plus £0.30. In the US it is roughly 13.25% plus $0.40 for sellers without a Store subscription. Rates vary by category and country.',
  },
  {
    q: 'Does the eBay fee include postage?',
    a: 'Yes. The Final Value Fee is calculated on the total amount the buyer pays, which includes the item price and any postage you charge. This calculator applies the fee to the sale price you enter, so enter the full amount the buyer pays for the most accurate result.',
  },
  {
    q: 'How does an eBay Store subscription reduce fees?',
    a: 'An eBay Store subscription lowers your Final Value Fee percentage by a fixed number of percentage points in most categories. Toggle the "I have an eBay Store" option to see your discounted rate. The exact saving depends on your country and category.',
  },
  {
    q: 'Is there a maximum eBay fee per item?',
    a: 'Yes. eBay caps the Final Value Fee per item in many categories. Once the percentage-based fee reaches the cap, you do not pay more on the portion above it. This calculator automatically applies the per-item cap for the selected marketplace.',
  },
  {
    q: 'Are these eBay fee rates up to date?',
    a: 'These rates were verified in June 2026 for standard categories under eBay Managed Payments. eBay updates its fee structure periodically and rates differ by category, so always confirm against eBay’s official fee schedule before pricing high-value items.',
  },
];

const EbayFeeCalculator: React.FC = () => {
  const [marketCode, setMarketCode] = useState('UK');
  const [salePrice, setSalePrice] = useState('');
  const [result, setResult] = useState<ReturnType<typeof calculateFees> | null>(null);

  // New UI fields — calculation logic unchanged (feeRates.ts untouched, store toggle removed)
  const [sellerType, setSellerType] = useState<'private' | 'business'>('business');
  const [category, setCategory] = useState<string>('most');
  const [shippingCharged, setShippingCharged] = useState<string>('');
  const [productCost, setProductCost] = useState<string>('');
  const [promotionRate, setPromotionRate] = useState<string>('');
  const isVatRegistered = false; // VAT folded into VAT-inclusive FVF rates (no separate VAT)
  const [isTopRatedPlus, setIsTopRatedPlus] = useState<boolean>(false);
  const [isInternational, setIsInternational] = useState<boolean>(false);

  const selectedMarket = MARKETS.find((m) => m.code === marketCode);

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="eBay Fee Calculator 2026 — UK, US, DE, AU | Coderom"
        description="Free eBay fee calculator for UK, US, Germany and Australia. Calculate final value fees, fixed fees and net profit instantly. Updated for 2026 Managed Payments rates."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.coderom.com/" },
            { "@type": "ListItem", "position": 2, "name": "eBay Fee Calculator", "item": "https://www.coderom.com/tools/ebay-fee-calculator" },
          ],
        }) }}
      />
      <Navbar />

      <div className="tools-page">
        <div className="calc-hero">
            <div className="calc-hero-bg" />
            <div className="calc-hero-content">
              <div className="hero-breadcrumb">
                <a href="/">Home</a>
                <span>›</span>
                <span>Tools</span>
                <span>›</span>
                <span>eBay Fee Calculator</span>
              </div>
              <div className="hero-eyebrow">
                <i className="ri-calculator-line" aria-hidden="true"></i>
                Free Tool
              </div>
              <h1>eBay Fee <span>Calculator</span> 2026</h1>
              <p>Calculate exact eBay fees for UK, US, Germany and Australia — VAT-inclusive rates, updated June 2026.</p>
              <div className="hero-badges">
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  VAT-inclusive rates
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Category rates
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Free tool
                </span>
              </div>
            </div>
          </div>

        <div className="calc-wrapper">
          {/* Market tabs */}
          <div className="market-tabs">
            {MARKETS.map(m => (
              <button
                key={m.code}
                className={`market-tab${selectedMarket?.code === m.code ? ' active' : ''}`}
                onClick={() => { setMarketCode(m.code); setResult(null); }}
              >
                <img
                  src={m.flagSrc}
                  alt={m.code}
                  style={{ width: 20, height: 15, borderRadius: 2, objectFit: 'cover' }}
                />
                <span>{m.code === 'UK' ? 'United Kingdom'
                  : m.code === 'US' ? 'United States'
                  : m.code === 'DE' ? 'Germany'
                  : 'Australia'}</span>
              </button>
            ))}
          </div>

          <div className="calc-card">
            {/* Seller settings */}
            <div className="section-label">Seller settings</div>
            <div className="form-grid-2">
              <div className="form-field">
                <label>Seller type</label>
                <div className="seller-toggle">
                  <button
                    className={`seller-btn${sellerType === 'private' ? ' active' : ''}`}
                    onClick={() => { setSellerType('private'); setResult(null); }}
                  >Private</button>
                  <button
                    className={`seller-btn${sellerType === 'business' ? ' active' : ''}`}
                    onClick={() => { setSellerType('business'); setResult(null); }}
                  >Business</button>
                </div>
              </div>
              <div className="form-field">
                <label>Item category</label>
                <div className="calc-input-wrap">
                  <select
                    value={category}
                    onChange={e => { setCategory(e.target.value); setResult(null); }}
                  >
                    <option value="most">Most categories</option>
                    <option value="electronics">Electronics &amp; Computers</option>
                    <option value="clothing">Clothing &amp; Shoes</option>
                    <option value="home">Home &amp; Garden</option>
                    <option value="toys">Toys &amp; Games</option>
                    <option value="health">Health &amp; Beauty</option>
                    <option value="sports">Sports &amp; Leisure</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vat-row">
              <input
                type="checkbox"
                id="topRatedCheck"
                checked={isTopRatedPlus}
                onChange={e => { setIsTopRatedPlus(e.target.checked); setResult(null); }}
                style={{ width: 16, height: 16, accentColor: '#f97316' }}
              />
              <label htmlFor="topRatedCheck">
                Top Rated Plus seller — 10% discount on final value fee
              </label>
            </div>
            <div className="vat-row">
              <input
                type="checkbox"
                id="intlCheck"
                checked={isInternational}
                onChange={e => { setIsInternational(e.target.checked); setResult(null); }}
                style={{ width: 16, height: 16, accentColor: '#f97316' }}
              />
              <label htmlFor="intlCheck">
                International payment — additional 1.65% fee applies
              </label>
            </div>

            <div className="divider" />

            {/* Pricing inputs */}
            <div className="section-label">Pricing</div>
            <div className="form-grid-4">
              <div className="form-field">
                <label>Sale price</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={salePrice}
                    onChange={e => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        setSalePrice(e.target.value);
                        setResult(null);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Product cost</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={productCost}
                    onChange={e => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        setProductCost(e.target.value);
                        setResult(null);
                      }
                    }}
                  />
                </div>
                <div className="field-note">Amazon cost price</div>
              </div>
              <div className="form-field">
                <label>Shipping charged</label>
                <div className="calc-input-wrap">
                  <span className="calc-input-affix">{selectedMarket?.currencySymbol}</span>
                  <input
                    type="text"
                    placeholder="0.00"
                    value={shippingCharged}
                    onChange={e => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        setShippingCharged(e.target.value);
                        setResult(null);
                      }
                    }}
                  />
                </div>
                <div className="field-note">eBay fees apply to this too</div>
              </div>
              <div className="form-field">
                <label>Promoted listing</label>
                <div className="calc-input-wrap">
                  <input
                    type="text"
                    placeholder="0"
                    value={promotionRate}
                    style={{ textAlign: 'right' }}
                    onChange={e => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        setPromotionRate(e.target.value);
                        setResult(null);
                      }
                    }}
                  />
                  <span className="calc-input-affix right">%</span>
                </div>
                <div className="field-note">Optional</div>
              </div>
            </div>

            {/* Calculate button */}
            <button
              className="calc-btn"
              disabled={!salePrice || parseFloat(salePrice) <= 0}
              onClick={() => {
                if (!selectedMarket) return;
                const price = parseFloat(salePrice) || 0;
                const shipping = parseFloat(shippingCharged) || 0;
                const totalSale = price + shipping;
                const opts: FeeCalcOpts = { category, sellerType, isVatRegistered, isTopRatedPlus };
                const calcResult = calculateFees(totalSale, selectedMarket, opts);
                setResult(calcResult);
              }}
            >
              Calculate eBay fees →
            </button>

            {/* Results */}
            {result && selectedMarket && (() => {
              const cost = parseFloat(productCost) || 0;
              const promo = parseFloat(promotionRate) || 0;
              const price = parseFloat(salePrice) || 0;
              const shipping = parseFloat(shippingCharged) || 0;
              const sym = selectedMarket.currencySymbol;

              // Promotion fee (still component-level)
              const promoFee = parseFloat(
                ((price + shipping) * promo / 100).toFixed(2)
              );

              // International fee (still component-level)
              const intlFee = isInternational
                ? parseFloat(
                    ((price + shipping) * 0.0165).toFixed(2)
                  )
                : 0;

              // calculateFees now includes: FVF + regulatory + VAT + Top Rated
              // totalFee from result is the authoritative base
              const totalFees = parseFloat(
                (result.totalFee + promoFee + intlFee).toFixed(2)
              );
              const netReceived = parseFloat(
                (price + shipping - totalFees).toFixed(2)
              );
              const netProfit =
                cost > 0
                  ? parseFloat((price + shipping - totalFees - cost).toFixed(2))
                  : null;

              return (
                <div className="results-grid">
                  <div className="result-card rc-neutral">
                    <div className="rc-label">FVF rate</div>
                    <div className="rc-val">{result.fvfPercent}%</div>
                    <div className="rc-sub">final value fee</div>
                  </div>
                  <div className="result-card rc-fee">
                    <div className="rc-label">Final value fee</div>
                    <div className="rc-val">{sym}{result.fvfAmount.toFixed(2)}</div>
                  </div>
                  <div className="result-card rc-fee">
                    <div className="rc-label">Fixed fee</div>
                    <div className="rc-val">{sym}{result.fixedFee.toFixed(2)}</div>
                  </div>
                  {result.regulatoryFee > 0 && (
                    <div className="result-card rc-fee">
                      <div className="rc-label">Regulatory fee</div>
                      <div className="rc-val">{sym}{result.regulatoryFee.toFixed(2)}</div>
                      <div className="rc-sub">UK 0.42%</div>
                    </div>
                  )}
                  {result.vatAmount > 0 && (
                    <div className="result-card rc-fee">
                      <div className="rc-label">VAT on fees</div>
                      <div className="rc-val">{sym}{result.vatAmount.toFixed(2)}</div>
                      <div className="rc-sub">20% on eBay fees</div>
                    </div>
                  )}
                  {promoFee > 0 && (
                    <div className="result-card rc-fee">
                      <div className="rc-label">Promotion fee</div>
                      <div className="rc-val">{sym}{promoFee.toFixed(2)}</div>
                    </div>
                  )}
                  {intlFee > 0 && (
                    <div className="result-card rc-fee">
                      <div className="rc-label">International fee</div>
                      <div className="rc-val">{sym}{intlFee.toFixed(2)}</div>
                    </div>
                  )}
                  <div className="result-card rc-fee">
                    <div className="rc-label">Total fees</div>
                    <div className="rc-val">{sym}{totalFees.toFixed(2)}</div>
                  </div>
                  <div className="result-card rc-net">
                    <div className="rc-label">You receive</div>
                    <div className="rc-val">{sym}{netReceived.toFixed(2)}</div>
                    <div className="rc-sub">after all fees</div>
                  </div>
                  {netProfit !== null && (
                    <div className={`result-card ${netProfit >= 0 ? 'rc-profit' : 'rc-loss'}`}>
                      <div className="rc-label">Net profit</div>
                      <div className="rc-val">{sym}{netProfit.toFixed(2)}</div>
                      <div className="rc-sub">after cost + fees</div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* How It Works */}
        <section className="how-it-works">
          <Container>
            <h2>How eBay Fees Are Calculated</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-num">1</div>
                <h3>Final Value Fee</h3>
                <p>
                  eBay takes a percentage of the total sale price (item + postage). The rate depends
                  on your marketplace and category — for example 12.9% in the UK or 13.25% in the US.
                </p>
              </div>
              <div className="step-card">
                <div className="step-num">2</div>
                <h3>Fixed Order Fee</h3>
                <p>
                  A small fixed fee is added once per order, regardless of price — typically £0.30 in
                  the UK or $0.40 in the US. It is included in your total eBay fees here.
                </p>
              </div>
              <div className="step-card">
                <div className="step-num">3</div>
                <h3>Per-Item Cap &amp; Net</h3>
                <p>
                  The percentage fee is capped per item in most categories. We subtract the total
                  fees from your sale price to show exactly what you receive in your account.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ */}
        <section className="tools-faq">
          <Container>
            <h2>eBay Fees — Frequently Asked Questions</h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, idx) => (
                <div className="faq-row" key={idx}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* RELATED LINKS */}
        <section className="tools-related">
          <Container>
            <h2>Keep exploring</h2>
            <div className="related-links-grid">
              <a href="/blog/ebay-repricing-strategies" className="related-link-card">
                <span className="rl-label">Guide</span>
                <span className="rl-title">eBay Repricing Strategies</span>
              </a>
              <a href="/tools/profit-calculator" className="related-link-card">
                <span className="rl-label">Free Tool</span>
                <span className="rl-title">Profit Calculator</span>
              </a>
              <a href="/tools/title-optimizer" className="related-link-card">
                <span className="rl-label">Free Tool</span>
                <span className="rl-title">eBay Title Optimizer</span>
              </a>
              <a href="/compare/coderom-vs-autods" className="related-link-card">
                <span className="rl-label">Compare</span>
                <span className="rl-title">Coderom vs AutoDS</span>
              </a>
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="tools-cta">
          <Container>
            <div className="cta-inner">
              <h2>Automate your dropshipping</h2>
              <p>Coderom calculates fees, reprices listings and syncs stock 24/7 — so every sale stays profitable.</p>
              <a href={`${APP_URL}/register`} className="cta-btn">Start Free Trial</a>
            </div>
          </Container>
        </section>
      </div>

      <Footer />
      <GuestChatWidget />

      <style>{`
        .hero-breadcrumb { font-size: 12px; color: #94a3b8; margin-bottom: 12px; display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
        .hero-breadcrumb a { color: #cbd5e1; text-decoration: none; }
        .hero-breadcrumb a:hover { color: #fb923c; }
        .tools-related { padding: 40px 0; }
        .tools-related h2 { font-size: 24px; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
        .related-links-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .related-link-card { display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: border-color 0.15s; }
        .related-link-card:hover { border-color: #f97316; }
        .rl-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #f97316; }
        .rl-title { font-size: 13px; font-weight: 500; color: #1e293b; line-height: 1.4; }
        .tools-page { min-height: 100vh; background: #f8fafc; padding: 0; }

        .calc-wrapper { max-width: 860px; margin: 0 auto; padding: 2rem 1rem; }
        .calc-hero {
          position: relative;
          overflow: hidden;
          border-radius: 0;
          margin-bottom: 2rem;
          padding: 80px 0 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .calc-hero-bg {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(135deg,
              rgba(15,23,42,0.72) 0%,
              rgba(15,23,42,0.55) 100%
            ),
            url('/images/tools/hero-fee-calculator.jpg')
            center/cover no-repeat;
        }
        .calc-hero-content {
          position: relative;
          z-index: 1;
          padding: 2.5rem 2rem;
          max-width: 680px;
          width: 100%;
          text-align: center;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 14px;
          background: rgba(249,115,22,0.2);
          border: 1px solid rgba(249,115,22,0.4);
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          color: #fb923c;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .calc-hero h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .calc-hero h1 span { color: #fb923c; }
        .calc-hero p {
          font-size: 1rem;
          color: #cbd5e1;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          line-height: 1.6;
        }
        .hero-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .hero-badge {
          padding: 4px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          font-size: 13px;
          font-weight: 500;
          color: #e2e8f0;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .hero-badge i { font-size: 12px; color: #fb923c; }
        .market-tabs { display: flex; gap: 8px; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .market-tab { padding: 8px 18px; border-radius: 999px; border: 1.5px solid #e2e8f0;
          font-size: 14px; font-weight: 500; cursor: pointer; color: #64748b;
          background: #fff; display: flex; align-items: center; gap: 6px;
          transition: all 0.15s; }
        .market-tab.active { background: #f97316; border-color: #f97316; color: #fff; }
        .market-tab:hover:not(.active) { border-color: #f97316; color: #f97316; }
        .calc-card { background: #fff; border: 1px solid #e2e8f0;
          border-radius: 16px; padding: 1.75rem; }
        .section-label { font-size: 11px; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.07em; color: #94a3b8; margin-bottom: 0.875rem; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.25rem; }
        .form-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 1rem; }
        .form-field { display: flex; flex-direction: column; gap: 5px; }
        .form-field label { font-size: 13px; color: #64748b; font-weight: 500; }
        .calc-input-wrap { display: flex; align-items: center; border: 1px solid #e2e8f0;
          border-radius: 8px; overflow: hidden; background: #fff; }
        .calc-input-wrap:focus-within { border-color: #f97316;
          box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }
        .calc-input-affix { padding: 0 10px; font-size: 14px; color: #94a3b8;
          background: #f8fafc; height: 40px; display: flex; align-items: center;
          border-right: 1px solid #e2e8f0; white-space: nowrap; }
        .calc-input-affix.right { border-right: none; border-left: 1px solid #e2e8f0; }
        .calc-input-wrap input, .calc-input-wrap select { border: none; outline: none;
          padding: 0 12px; height: 40px; font-size: 15px; color: #1e293b;
          background: transparent; width: 100%; }
        .seller-toggle { display: flex; gap: 6px; }
        .seller-btn { flex: 1; padding: 9px; border-radius: 8px;
          border: 1.5px solid #e2e8f0; font-size: 13px; font-weight: 500;
          cursor: pointer; color: #64748b; background: #fff; text-align: center;
          transition: all 0.15s; }
        .seller-btn.active { border-color: #f97316; color: #f97316; background: #fff7ed; }
        .vat-row { display: flex; align-items: center; gap: 10px;
          padding: 10px 0; margin-top: 0.25rem; }
        .vat-row label { font-size: 14px; color: #475569; cursor: pointer; flex: 1; }
        .divider { border: none; border-top: 1px solid #f1f5f9; margin: 1.5rem 0; }
        .field-note { font-size: 11px; color: #94a3b8; margin-top: 3px; }
        .calc-btn { width: 100%; margin-top: 1.5rem; padding: 13px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: #fff; border: none; border-radius: 10px; font-size: 15px;
          font-weight: 600; cursor: pointer; display: flex; align-items: center;
          justify-content: center; gap: 8px; transition: opacity 0.15s; }
        .calc-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .calc-btn:not(:disabled):hover { opacity: 0.9; }
        .results-grid { display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px; margin-top: 1.5rem; }
        .result-card { border-radius: 10px; padding: 14px 16px; }
        .result-card .rc-label { font-size: 12px; color: #64748b; margin-bottom: 6px; }
        .result-card .rc-val { font-size: 22px; font-weight: 600; color: #1e293b; }
        .result-card .rc-sub { font-size: 11px; color: #94a3b8; margin-top: 3px; }
        .rc-neutral { background: #f8fafc; border: 1px solid #e2e8f0; }
        .rc-fee { background: #fef2f2; border: 1px solid #fecaca; }
        .rc-fee .rc-val { color: #dc2626; }
        .rc-net { background: #f0fdf4; border: 1px solid #bbf7d0; }
        .rc-net .rc-val { color: #16a34a; }
        .rc-profit { background: #eff6ff; border: 1px solid #bfdbfe; }
        .rc-profit .rc-val { color: #2563eb; }
        .rc-loss { background: #fef2f2; border: 1px solid #fecaca; }
        .rc-loss .rc-val { color: #dc2626; }

        /* --- Preserved SEO sections (How It Works / FAQ / CTA), moved below calculator --- */
        .how-it-works { padding: 70px 0; background: #fff; margin-top: 48px; }
        .how-it-works h2, .tools-faq h2 {
          text-align: center; font-size: 30px; font-weight: 800; color: #1e293b; margin-bottom: 40px;
        }
        .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 980px; margin: 0 auto; }
        .step-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 28px; }
        .step-num {
          width: 40px; height: 40px; border-radius: 10px;
          background: linear-gradient(135deg, #f97316, #fb923c); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 18px; margin-bottom: 16px;
        }
        .step-card h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
        .step-card p { font-size: 14px; color: #64748b; line-height: 1.65; margin: 0; }

        .tools-faq { padding: 70px 0; }
        .faq-list { max-width: 800px; margin: 0 auto; }
        .faq-row { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 22px 24px; margin-bottom: 14px; }
        .faq-row h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
        .faq-row p { font-size: 14px; color: #64748b; line-height: 1.7; margin: 0; }

        .tools-cta { padding: 80px 0; }
        .cta-inner {
          max-width: 720px; margin: 0 auto; text-align: center;
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-radius: 20px; padding: 56px 32px; color: #fff;
        }
        .cta-inner h2 { font-size: 30px; font-weight: 800; margin-bottom: 12px; }
        .cta-inner p { font-size: 17px; opacity: 0.92; margin-bottom: 28px; }
        .cta-btn {
          display: inline-block; padding: 15px 36px; background: #fff; color: #ea580c;
          font-weight: 700; font-size: 16px; border-radius: 12px; text-decoration: none;
          transition: all 0.2s;
        }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,0,0,0.2); color: #ea580c; }

        @media (max-width: 768px) {
          .form-grid-2, .form-grid-4 { grid-template-columns: 1fr; }
          .steps-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default EbayFeeCalculator;
